/**
 * Admin mutations, client side.
 *
 * Each function keeps the exact signature its server-action ancestor had —
 * `(formData: FormData) => Promise<void>` — which is why the admin pages still
 * write `<form action={priceQuoteAction}>` and did not have to change at all.
 * React 19 accepts a plain async function as a form action; it never needed to
 * be a server action to work.
 *
 * `revalidatePath` becomes an explicit `invalidateQueries`. That is more code,
 * but it says which screens a mutation actually affects instead of asking the
 * framework to work it out.
 */

import { queryClient } from '@/query-client';

async function run(action: string, formData: FormData): Promise<void> {
  const res = await fetch(`/api/admin/${action}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } }
      | null;
    throw new Error(body?.error?.message ?? `Action ${action} failed`);
  }
}

const invalidate = (...keys: string[][]) =>
  Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));

export async function priceQuoteAction(formData: FormData) {
  await run('priceQuote', formData);
  await invalidate(['admin', 'quote'], ['admin', 'quotes'], ['admin', 'shell']);
}

export async function declineQuoteAction(formData: FormData) {
  await run('declineQuote', formData);
  await invalidate(['admin', 'quote'], ['admin', 'quotes'], ['admin', 'shell']);
}

export async function addProofAction(formData: FormData) {
  await run('addProof', formData);
  await invalidate(['admin', 'quote'], ['admin', 'quotes'], ['quote'], ['admin', 'shell']);
}

export async function convertQuoteAction(formData: FormData) {
  await run('convertQuote', formData);
  await invalidate(
    ['admin', 'quote'],
    ['admin', 'quotes'],
    ['admin', 'orders'],
    // No commission key: there is no commission screen and no ledger table.
    // T024f, R-165.
    ['admin', 'dashboard'],
  );
}

export async function transitionOrderAction(formData: FormData) {
  await run('transitionOrder', formData);
  await invalidate(['admin', 'orders'], ['admin', 'dashboard'], ['order'], ['orders']);
}

export async function updateMethodAction(formData: FormData) {
  await run('updateMethod', formData);
  // Rates feed the studio and every catalogue price, so this one is broad.
  await invalidate(['admin', 'methods'], ['studio'], ['custom'], ['home'], ['product']);
}

export async function createCampaignAction(formData: FormData) {
  await run('createCampaign', formData);
  await invalidate(['admin', 'campaigns']);
}

export async function updateSiteConfigAction(formData: FormData) {
  await run('updateSiteConfig', formData);
  await invalidate(['admin', 'campaigns'], ['home'], ['product'], ['order']);
}

